import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  CreditCard,
  Lock,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import {Card, Skeleton, cn} from '@chekinapp/ui';
import {formatDisplayDate} from '../guestForm/helpers';
import {useRemoteAccessEnabled} from '../remoteAccess/hooks';
import {useHomeNavigate} from './env';
import {useHomeData} from './HomeDataContext';
import type {ChecklistStep} from './useChecklistSteps';

const STEP_ICON: Record<ChecklistStep['id'], LucideIcon> = {
  guest_details: UserCheck,
  payments: CreditCard,
};

const HEADER_CLASS =
  'flex items-center justify-between gap-3 px-4 py-[11px] text-[var(--chekin-color-white)] [background:var(--chekin-gradient-duotone)]';

const ROW_DIVIDER =
  'relative after:absolute after:left-[64px] after:right-0 after:top-0 after:h-px after:bg-[var(--chekin-divider-subtle)] after:content-[""] first:after:hidden lg:after:left-[68px]';

function ChecklistSkeleton() {
  return (
    <section data-testid="checkin-checklist-skeleton" aria-hidden>
      <Card className="overflow-hidden p-0">
        <div className={HEADER_CLASS}>
          <Skeleton className="h-4 w-52 bg-white/20" />
          <Skeleton className="h-6 w-32 rounded-full bg-white/20" />
        </div>
        {[0, 1].map(i => (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5 lg:px-4">
            <Skeleton className="size-10 shrink-0 rounded-[12px]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="h-8 w-20 shrink-0 rounded-[12px]" />
          </div>
        ))}
      </Card>
    </section>
  );
}

export function CheckinChecklist() {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const go = useHomeNavigate()(navigate);
  const {navItems, navLoading, checkInDate} = useHomeData();
  const hasKeys = useRemoteAccessEnabled();

  if (navLoading) return <ChecklistSkeleton />;
  if (navItems.length === 0) return null;

  const remaining = navItems.filter(step => !step.completed);
  const allDone = remaining.length === 0;
  const nextStepId = remaining[0]?.id;
  const dueDate = formatDisplayDate(checkInDate, i18n.language, {
    day: 'numeric',
    month: 'short',
  });

  const heading = allDone
    ? t('checklist_completed_title')
    : t(hasKeys ? 'checklist_steps_to_unlock_keys' : 'checklist_steps_to_complete', {
        count: remaining.length,
      });

  return (
    <section data-testid="checkin-checklist">
      <Card className="overflow-hidden p-0">
        <div className={HEADER_CLASS}>
          <div className="flex min-w-0 items-center gap-2">
            {allDone ? (
              <Check
                size={16}
                strokeWidth={3}
                className="shrink-0 text-[var(--chekin-green-500)]"
                aria-hidden
              />
            ) : (
              <Lock
                size={16}
                className="shrink-0 text-[var(--chekin-blue-200)]"
                aria-hidden
              />
            )}
            <div className="min-w-0">
              <h2
                id="checkin-checklist-title"
                className="text-[14px] font-semibold leading-[18px] tracking-[-0.005em]"
              >
                {heading}
              </h2>
              {allDone && (
                <p className="text-[12px] leading-4 text-white/80">
                  {t('checklist_completed_subtitle')}
                </p>
              )}
            </div>
          </div>
          {!allDone && dueDate && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/[0.14] px-2.5 py-1 text-[12px] font-medium leading-[18px]">
              <Clock size={15} className="text-[var(--chekin-blue-200)]" aria-hidden />
              <span className="sr-only sm:not-sr-only">
                {t('checklist_due_before', {date: dueDate})}
              </span>
              <span aria-hidden className="sm:hidden">
                {dueDate}
              </span>
            </span>
          )}
        </div>

        <ul
          role="list"
          aria-labelledby="checkin-checklist-title"
          className="m-0 list-none p-0"
        >
          {navItems.map(step => (
            <li key={step.testId} className={ROW_DIVIDER}>
              <ChecklistRow
                step={step}
                onGo={() =>
                  go(step.path, 'checklist', {
                    task: step.id,
                    is_next_step: step.id === nextStepId,
                  })
                }
              />
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

type ChecklistRowProps = {
  step: ChecklistStep;
  onGo: () => void;
};

function ChecklistRow({step, onGo}: ChecklistRowProps) {
  const {t} = useTranslation();
  const Icon = STEP_ICON[step.id];
  const done = step.completed;
  const {key, values} = step.subtitle;

  return (
    <button
      type="button"
      onClick={onGo}
      data-testid={step.testId}
      data-state={done ? 'done' : 'pending'}
      className={cn(
        'group relative flex min-h-14 w-full items-center gap-3 px-3 py-2.5 text-left transition-colors lg:px-4',
        'hover:bg-[var(--chekin-neutral-50)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--chekin-color-brand-blue)]',
      )}
    >
      <span className="relative grid size-10 shrink-0 place-items-center rounded-[12px] bg-[var(--chekin-blue-tint-info)] text-[var(--chekin-color-brand-blue)]">
        <Icon size={20} aria-hidden />
        <span
          aria-hidden
          className={cn(
            'absolute -bottom-[7px] -right-[7px] grid size-5 place-items-center rounded-full border-2 border-[var(--chekin-color-white)] text-[12px] font-extrabold leading-none lg:hidden',
            done
              ? 'bg-[var(--chekin-green-500)] text-[var(--chekin-color-brand-navy)]'
              : 'bg-[var(--chekin-amber-400)] text-[var(--chekin-color-brand-navy)]',
          )}
        >
          {done ? <Check size={11} strokeWidth={3.4} /> : '!'}
        </span>
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[15px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)]">
          {t(step.title)}
          <span
            className={cn(
              'ml-2 hidden rounded-full px-2 align-[1px] text-[12px] font-extrabold uppercase leading-[18px] tracking-[0.08em] lg:inline-block',
              done
                ? 'bg-[var(--chekin-green-500)] text-[var(--chekin-color-brand-navy)]'
                : 'bg-[var(--chekin-amber-400)] text-[var(--chekin-color-brand-navy)]',
            )}
          >
            {t(done ? 'checklist_status_done' : 'checklist_status_pending')}
          </span>
        </span>
        <span className="mt-0.5 text-[13px] leading-[17px] text-[var(--chekin-text-secondary)]">
          {values ? t(key, values) : t(key)}
        </span>
      </span>

      {done ? (
        <ChevronRight
          size={22}
          aria-hidden
          className="ml-1.5 shrink-0 text-[var(--chekin-text-tertiary)] transition-[transform,color] group-hover:translate-x-[3px] group-hover:text-[var(--chekin-color-brand-blue)]"
        />
      ) : (
        <span
          aria-hidden
          className="ml-2 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[12px] bg-[var(--chekin-color-brand-blue)] px-3 text-[13px] font-semibold text-[var(--chekin-color-white)] transition-colors group-hover:bg-[var(--chekin-color-brand-hover)] lg:px-3.5"
        >
          {step.cta.values ? t(step.cta.key, step.cta.values) : t(step.cta.key)}
          <ArrowRight size={15} className="hidden lg:block" />
        </span>
      )}
    </button>
  );
}
